import {
  terraProxyApiClient,
  type FindingsBulkFilter,
} from '../../clients/testerClient';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { InternalEnrichedFinding } from '@terra/ui-design-system';
import { PaginationDefaults } from '@terra/shared-dto-types';
import type { FindingsListSelection } from '@terra/shared-contracts-findings';
import {
  buildFindingsListEnrichedQueryForTarget,
  buildFindingsListNavigationQueryForTarget,
  mapFindingListEnrichedToInternal,
  type FindingsListEnrichedQueryOptions,
} from '../../clients/tester-client/findings-list-enriched-query';

type FindingsListSelectionQuery = Extract<
  FindingsListSelection,
  { allSelected: true }
>['query'];

export async function countFindingsForTarget({
  targetId,
  targetType,
  filter = {},
  excludedIds = [],
}: {
  targetId: string;
  targetType: TargetTypeType;
  filter?: FindingsBulkFilter;
  excludedIds?: string[];
}): Promise<number> {
  return terraProxyApiClient.countFindingsByTarget({
    targetId,
    targetType,
    filter,
    excludedIds,
  });
}

export async function fetchFindingsListEnrichedPageForTarget({
  targetId,
  targetType,
  options,
}: {
  targetId: string;
  targetType: TargetTypeType;
  options?: FindingsListEnrichedQueryOptions;
}): Promise<{ findings: InternalEnrichedFinding[]; total: number }> {
  const response = await terraProxyApiClient.getAllFindingsEnriched(
    buildFindingsListEnrichedQueryForTarget({
      targetId,
      targetType,
      options,
    }),
  );

  return {
    findings: response.data.map(mapFindingListEnrichedToInternal),
    total: response.total,
  };
}

export async function fetchFindingsPageForTarget({
  targetId,
  targetType,
  options,
}: {
  targetId: string;
  targetType: TargetTypeType;
  options?: FindingsListEnrichedQueryOptions;
}) {
  const { findings } = await fetchFindingsListEnrichedPageForTarget({
    targetId,
    targetType,
    options,
  });

  return findings;
}

export async function fetchFindingsNavigationIdsForTarget({
  targetId,
  targetType,
  options,
}: {
  targetId: string;
  targetType: TargetTypeType;
  options?: Omit<
    FindingsListEnrichedQueryOptions,
    'skip' | 'limit' | 'findingIds'
  >;
}): Promise<string[]> {
  const query = buildFindingsListNavigationQueryForTarget({
    targetId,
    targetType,
    options,
  });
  const resolved = await terraProxyApiClient.resolveFindingsListSelection({
    allSelected: true,
    query: query as FindingsListSelectionQuery,
  });

  return resolved?.ids ?? [];
}

export async function fetchFindingsListForTarget({
  targetId,
  targetType,
  endpointId,
  options,
}: {
  targetId: string;
  targetType: TargetTypeType;
  endpointId?: string;
  options?: FindingsListEnrichedQueryOptions;
}): Promise<InternalEnrichedFinding[]> {
  const listOptions = {
    ...options,
    endpointId: endpointId ?? options?.endpointId,
  };

  return fetchFindingsPageForTarget({
    targetId,
    targetType,
    options: {
      ...listOptions,
      limit: listOptions.limit ?? PaginationDefaults.MAX_LIMIT,
    },
  });
}
